<template>
	<div class="eia-stage-container">

		<eia-stage-header :current-stage="currentStage"></eia-stage-header>

		<section class="eia-stage-one">
			<main v-if="chooseProposal">
				<div class="proposal-container">
					<v-select
						:options="selectOptions"
						v-model="selectedProposal"
						@input="onSelectedProposalChange"
						placeholder="Please select">
					</v-select>
				</div>

				<div class="custom-proposal-toggle-container">
					<eia-accordion :open="customProposalVisible"
						@toggle="onCustomProposalToggle">
						<template slot="title">Can't find your proposal type?</template>

						<div class="eia-indented"
							:class="customProposalIndentationClass">
							<eia-paragraph>No problem, just type your proposal below</eia-paragraph>

							<eia-validated-input
								type="text"
								:validators="validators"
								:show-error-indentation="false"
								placeholder=""
								v-model="customProposal"
								@input="onCustomProposalInput"
								@error="onCustomProposalError">
							</eia-validated-input>
						</div>
					</eia-accordion>
				</div>
			</main>

			<component v-if="question"
				:is="question.component"
				v-model="response"
				@input="onResponse"
				@currentStage="setQuestionStage"
				:question="question.question"></component>

			<component v-if="stage"
				:is="stage.component"
				v-model="response"
				:on-reset="reset"
				@input="onResponse"></component>

			<eia-stage-footer
				:clickBack="backClickEnabled ? backClick : null"
				:clickReset="resetClickEnabled ? resetClick : null"
				:click="nextClick"
				:next-stage-index="nextStageIndex"
				:disabled="nextButtonDisabled"
				:caption="true"
				:current-stage="currentStage">
			</eia-stage-footer>
		</section>

	</div>
</template>

<script>
import Select from '@/components/questions/Select';
import AreaMap from '@/components/questions/AreaMap';
import Radio from '@/components/questions/Radio';
import initialStages from '@/copy/initial-stages';
import finalStages from '@/copy/final-stages';
import { log } from '@/utils';
import { mapState, mapGetters } from 'vuex';
import {
	notEmpty,
	minLength,
} from '@/utils/validators';

const defaultState = {
	selectedProposal: '',
	customProposal: '',
	customProposalError: null,
	customProposalVisible: false,
};

export default {
	name: 'eia-proposals',

	data() {
		return {

			...defaultState,

			validators: [
				notEmpty,
				minLength( 5 ),
			],

			response: null,
			questionStage: {},
			stageOverride: null,
		};
	},

	beforeMount(){

		if( this.proposal ){
			if( this.proposal.id === 'custom' ){

				this.customProposal = this.proposal.description;
				this.customProposalVisible = true;

			}
			else{

				this.selectedProposal = {
					label: this.proposal.description,
					proposal: this.proposal,
				};

			}
		}

		this.setModifiedQuestionResponse();

	},

	watch:{

		// if question has changed, are we modifying?
		// do we need to set the response?
		question(){
			this.setModifiedQuestionResponse();
		},

	},

	computed: {
		...mapState( [ 'proposals' ] ),
		...mapState( 'stages', [ 'proposal', 'responses', 'initialStageIndex', 'finalStageIndex', 'modifiedStageIndex' ] ),
		...mapGetters( 'stages', [ 'currentStageIndex', 'modifiedResponseIndex', 'modifiedFinalStageIndex' ] ),

		chooseProposal(){
			return this.currentStageIndex === initialStages.length;
		},

		selectOptions(){

			return this.proposals.map( proposal => {
				return {
					label: proposal.description,
					proposal,
				};
			} );

		},

		question(){

			if( this.proposal && !this.chooseProposal && this.currentStageIndex > initialStages.length ){

				let questionId = 0;
				let component;
				let response = null;

				// check last response for question ID
				if( this.responses?.length ){

					let index = this.responses.length - 1;

					// are we modifying a completed question?
					if( this.modifiedStageIndex !== null ){
						index = this.modifiedResponseIndex - 1;
						response = this.responses[ index + 1 ];
					}

					questionId = index < 0 ? 0 : this.responses[ index ].result;
				}

				const question = this.proposal.questions[ questionId ];

				if( question ){

					switch( question.type ){

						case 'select':
							component = Select;
							break;

						case 'area-map':
							component = AreaMap;
							break;

						case 'radio':
							component = Radio;
							break;

					}

					return {
						question,
						component,
						response,
					};
				}
			}

			return null;
		},

		nextButtonDisabled() {

			if( this.question || this.stage ){
				return !this.response;
			}

			return !!(
				( !this.selectedProposal && !this.customProposal ) ||
				this.customProposalError
			);
		},

		customProposalIndentationClass() {
			return this.customProposalError ?
				'eia-indented--error' :
				undefined;
		},

		nextStageIndex() {
			return this.currentStageIndex + 1;
		},

		initialStage() {

			const index = this.modifiedStageIndex !== null ? this.modifiedStageIndex : this.initialStageIndex;

			if( index < initialStages.length ){
				return initialStages[ index ];
			}

			return null;
		},

		stage() {

			if( this.initialStage ){
				return this.initialStage;
			}

			if(	this.currentStageIndex > initialStages.length && !this.question ){

				const index = this.modifiedStageIndex !== null ? this.modifiedFinalStageIndex : this.finalStageIndex;

				return finalStages[ index ];
			}

			return null;
		},

		currentStage(){

			if( !this.chooseProposal ){

				if( this.question ){
					return this.questionStage;
				}

				if( this.stage.component.currentStage ){
					return this.stage.component.currentStage;
				}

				return this.stage;
			}

			return {
				header:'What are you proposing?',
			};

		},

		backClickEnabled(){

			// are we on the final stage?
			if( ( finalStages.length - 1 ) <= this.finalStageIndex ){
				return false;
			}

			return this.initialStageIndex > 0;
		},

		resetClickEnabled(){
			// are we on the final stage?
			if( ( finalStages.length - 1 ) <= this.finalStageIndex ){
				return false;
			}

			return this.initialStageIndex > 0;
		},

	},

	methods: {

		setQuestionStage( stage ){
			this.questionStage = stage;
		},

		setModifiedQuestionResponse(){
			if( this.modifiedStageIndex && this.question?.response ){
				this.response = this.question.response;
			}
		},

		resetClick(){
			return this.reset();
		},

		backClick(){

			if( !this.proposal ){
				console.log( 'stages/decrementInitialStageIndex' );

				return this.$store.commit( 'stages/decrementInitialStageIndex' );
			}
			else{

				// do we have a question?
				if( !this.question ){

					// have we progressed through the final stages?
					if( this.finalStageIndex ){
						console.log( 'stages/decrementFinalStageIndex' );

						return this.$store.commit( 'stages/decrementFinalStageIndex' );
					}

				}

				// have we answered any questions?
				if( this.responses.length ){
					console.log( 'stages/clearLastResponse' );

					// go back a question
					return this.$store.commit( 'stages/clearLastResponse' );
				}

				console.log( 'stages/clearChoice' );

				// clear proposal
				return this.$store.commit( 'stages/clearChoice' );

			}

		},

		nextClick(){

			window.scrollTo( {
				top: 0,
				left: 0,
				behavior: 'smooth',
			} );

			const payload = {};

			// are we on an initial stage or choosing a proposal?
			if( this.currentStageIndex <= initialStages.length ){

				// are we on an initial stage?
				if( this.stage ){
					this.response = null;

					// are we modifying a stage on review?
					if( this.modifiedStageIndex !== null ){
						return this.$store.commit( 'stages/modifyStageIndex' , null );
					}

					return this.$store.commit( 'stages/incrementInitialStageIndex' );
				}

				payload.proposal = this.selectedProposal?.proposal || { id: 'custom', description: this.customProposal, questions: [] };

				if( !payload.proposal ){
					delete payload.proposal;
				}

			}

			// are we dealing with questions or final stages?
			else{

				// store and clear response
				if( this.response ){

					// response is a function
					// this means the component has requested an additional step
					if( typeof this.response === 'function' ){
						const fn = this.response;

						this.response = null;

						// call callback, and pass in completion function to be used if required
						return fn( response => {
							this.response = response;
							this.nextClick();
						} );
					}

					// are we on the final stage?
					if( this.stage ){
						this.response = null;

						// are we modifying a final stage?
						if( this.modifiedStageIndex !== null ){
							return this.$store.commit( 'stages/modifyStageIndex', null );
						}

						return this.$store.commit( 'stages/incrementFinalStageIndex' );
					}

					payload.response = this.response;

					payload.response.question = {
						id: this.question.question.id,
						question: this.question.question.question,
					};

					this.response = null;
				}

			}

			if( Object.keys( payload ).length ){
				this.$store.commit( 'stages/makeChoice', payload );
			}

		},

		onResponse( value ){
			log( 'Response:', value );
		},

		onSelectedProposalChange( value ) {
			if( value !== null ) {
				this.customProposal = '';
				this.customProposalVisible = false;
			}
		},

		onCustomProposalInput() {
			this.selectedProposal = '';
		},

		onCustomProposalError( error, errorMessage ) {
			// When error is false, errorMessage is null.
			// We don't need any special logic here to reset the
			// `customProposalError` value.
			this.customProposalError = errorMessage;
		},

		onCustomProposalToggle( val ){
			this.customProposalVisible = val;
		},

		reset(){

			this.$store.commit( 'stages/reset' );

			for( const key of Object.keys( defaultState ) ){
				this[ key ] = defaultState[ key ];
			}

		},
	},
};
</script>

<style lang="scss" scoped>
main {
	/*margin: 32px 0;*/

	.eia-accordion {
		.eia-indented {
			margin-top: 16px;
		}
	}
}
.eia-stage-container {
}
</style>
